import { Blog, BlogListResponse, BlogListQuery } from '../types/blog';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

class BlogService {
  private baseUrl = `${API_BASE_URL}/blog`;

  /**
   * Get featured blogs for homepage
   */
  async getFeaturedBlogs(limit: number = 6): Promise<Blog[]> {
    try {
      const response = await fetch(`${this.baseUrl}/public/featured?limit=${limit}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data || data; // Handle different response formats
    } catch (error) {
      console.error('Error fetching featured blogs:', error);
      return [];
    }
  }

  /**
   * Get published blogs with filtering and pagination
   */
  async getPublishedBlogs(query: BlogListQuery = {}): Promise<BlogListResponse> {
    try {
      const searchParams = new URLSearchParams();
      
      // Ensure numeric values are properly set
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });

      const response = await fetch(`${this.baseUrl}/public?${searchParams}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('API Error:', errorData);
        throw new Error(`HTTP error! status: ${response.status} - ${JSON.stringify(errorData)}`);
      }
      
      const data = await response.json();
      console.log('getPublishedBlogs data', data.data);
      return data.data || data;
    } catch (error) {
      console.error('Error fetching published blogs:', error);
      // Return empty response structure for consistency
      return {
        blogs: [],
        total: 0,
        page: query.page || 1,
        limit: query.limit || 10,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      };
    }
  }

  /**
   * Get a single blog by slug
   */
  async getBlogBySlug(slug: string): Promise<Blog | null> {
    try {
      const response = await fetch(`${this.baseUrl}/public/${slug}`);
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blog = await response.json();
      const data = blog.data || blog;
      // Increment view count after successfully fetching the blog
      await this.incrementViewCount(slug);
      
      return data;
    } catch (error) {
      console.error('Error fetching blog by slug:', error);
      return null;
    }
  }

  /**
   * Get related blogs for a specific blog
   */
  async getRelatedBlogs(slug: string, limit: number = 4): Promise<Blog[]> {
    try {
      const response = await fetch(`${this.baseUrl}/public/${slug}/related?limit=${limit}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Error fetching related blogs:', error);
      return [];
    }
  }

  /**
   * Get all blog categories
   */
  async getCategories(): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseUrl}/public/categories`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  }

  /**
   * Get all blog tags
   */
  async getTags(): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseUrl}/public/tags`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Error fetching tags:', error);
      return [];
    }
  }

  /**
   * Increment view count for a blog
   */
  async incrementViewCount(slug: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/public/view`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier: slug }),
      });
      
      if (!response.ok) {
        console.warn('Failed to increment view count:', response.status);
      }
    } catch (error) {
      console.error('Error incrementing view count:', error);
      // Don't throw - this is not critical functionality
    }
  }

  /**
   * Format date for display
   */
  formatDate(date: Date | string): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Create excerpt from content if not provided
   */
  createExcerpt(content: string, maxLength: number = 150): string {
    // Remove HTML tags
    const textContent = content.replace(/<[^>]*>/g, '');
    
    if (textContent.length <= maxLength) {
      return textContent;
    }
    
    return textContent.substring(0, maxLength).trim() + '...';
  }

  /**
   * Generate SEO meta tags for a blog
   */
  generateMetaTags(blog: Blog): Array<{ name?: string; property?: string; content: string }> {
    const tags = [
      { name: 'description', content: blog.metaDescription || blog.excerpt },
      { name: 'keywords', content: blog.metaKeywords.join(', ') },
      { name: 'author', content: `${blog.author.firstName} ${blog.author.lastName}` },
      { property: 'og:title', content: blog.ogTitle || blog.title },
      { property: 'og:description', content: blog.ogDescription || blog.excerpt },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: `${window.location.origin}/blog/${blog.slug}` },
      { property: 'article:author', content: `${blog.author.firstName} ${blog.author.lastName}` },
      { property: 'article:published_time', content: blog.publishedAt ? new Date(blog.publishedAt).toISOString() : '' },
      { property: 'article:tag', content: blog.tags.join(', ') },
      { name: 'twitter:card', content: blog.twitterCard || 'summary_large_image' },
      { name: 'twitter:title', content: blog.ogTitle || blog.title },
      { name: 'twitter:description', content: blog.ogDescription || blog.excerpt },
    ];

    if (blog.featuredImage || blog.ogImage) {
      tags.push(
        { property: 'og:image', content: blog.ogImage || blog.featuredImage! },
        { name: 'twitter:image', content: blog.ogImage || blog.featuredImage! }
      );
    }

    if (blog.canonicalUrl) {
      tags.push({ name: 'canonical', content: blog.canonicalUrl });
    }

    return tags.filter(tag => tag.content && tag.content.trim() !== '');
  }

  /**
   * Apply meta tags to document head
   */
  applyMetaTags(blog: Blog): void {
    const metaTags = this.generateMetaTags(blog);
    
    // Remove existing meta tags
    const existingTags = document.querySelectorAll('meta[name], meta[property]');
    existingTags.forEach(tag => {
      const name = tag.getAttribute('name') || tag.getAttribute('property');
      if (name && (name.startsWith('og:') || name.startsWith('twitter:') || 
          ['description', 'keywords', 'author', 'canonical'].includes(name))) {
        tag.remove();
      }
    });

    // Add new meta tags
    metaTags.forEach(({ name, property, content }) => {
      const meta = document.createElement('meta');
      if (name) meta.setAttribute('name', name);
      if (property) meta.setAttribute('property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    });

    // Set page title
    document.title = `${blog.title} | MockTrail Blog`;

    // Add structured data
    if (blog.structuredData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(blog.structuredData);
      document.head.appendChild(script);
    }
  }
}

export const blogService = new BlogService();
export default blogService; 